/**
 * Hook para carregar histórico de mensagens do webchat
 * Usa Edge Function webchat-history com paginação por cursor
 * 
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ChatMessage } from '../types';
import { getSupabaseUrl } from '../utils/supabaseConfig';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger centralizado - evita expor stack/dados sensíveis no console em produção
const logger = createLogger('UaizyChatHistory');

// Interface de props do hook
interface UseMessageHistoryProps {
  visitorId: string | null;
  inboxId: string;
  enabled: boolean;  // Só carrega se true
}

// Interface de retorno do hook
interface UseMessageHistoryReturn {
  messages: ChatMessage[];
  conversationId: string | null;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  hasStarted: boolean;  // Indica se o fetch já foi iniciado (diferencia de estado inicial)
  loadMore: () => Promise<void>;
  refetch: () => void;
}

// Interface de resposta da Edge Function
interface HistoryResponse {
  success: boolean;
  conversation_id: string | null;
  messages: ChatMessage[];
  next_cursor: string | null;
  has_more: boolean;
  error?: string;
}

export const useMessageHistory = ({
  visitorId,
  inboxId,
  enabled,
}: UseMessageHistoryProps): UseMessageHistoryReturn => {
  // Estados
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // NOVO: Flag que indica se o fetch já foi iniciado pelo menos uma vez
  // Diferencia "estado inicial" de "fetch concluiu com array vazio"
  const [hasStarted, setHasStarted] = useState(false);

  // Ref para evitar chamadas duplicadas
  const hasFetched = useRef(false);
  const isLoadingMore = useRef(false);

  // Reseta flags quando visitorId ou inboxId mudam
  // Isso permite recarregar histórico após refresh (CTRL+R) ou troca de tenant
  useEffect(() => {
    hasFetched.current = false;
    setMessages([]);
    setConversationId(null);
    setError(null);
    setHasMore(false);
    setNextCursor(null);
    setHasStarted(false);  // Reset também o hasStarted
  }, [visitorId, inboxId]);

  // Função para buscar histórico
  const fetchHistory = useCallback(async (cursor?: string | null) => {
    if (!visitorId || !inboxId) return;

    const supabaseUrl = getSupabaseUrl();
    if (!supabaseUrl) {
      logger.debug('URL do Supabase não configurada');
      setError('Configuração inválida');
      return;
    }

    try {
      // Obtém sessão atual para enviar token de autenticação
      // Isso permite validação server-side do visitor_id
      const { data: { session } } = await supabase.auth.getSession();
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      // Se há sessão, inclui Authorization header para validação server-side
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/webchat-history`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          visitor_id: visitorId,
          inbox_id: inboxId,
          cursor: cursor || null,
          limit: 30,
        }),
      });

      const data: HistoryResponse = await response.json();

      if (!response.ok || !data.success) {
        // Rate limit ou erro - não é crítico, pode tentar depois
        if (response.status === 429) {
          logger.debug('Rate limit atingido ao carregar histórico');
          setError('Muitas requisições. Tente novamente em alguns segundos.');
          return;
        }
        throw new Error(data.error || 'Falha ao carregar histórico');
      }

      return data;
    } catch (err) {
      logger.debug('Erro ao carregar histórico (silenciado)');
      throw err;
    }
  }, [visitorId, inboxId]);

  // Carrega histórico inicial quando o hook é habilitado
  useEffect(() => {
    // Só executa se habilitado, tem visitorId e ainda não carregou
    if (!enabled || !visitorId || hasFetched.current) return;

    const loadInitialHistory = async () => {
      hasFetched.current = true;
      setHasStarted(true);  // Marca que o fetch foi iniciado ANTES do await
      setIsLoading(true);
      setError(null);

      try {
        const data = await fetchHistory();
        
        if (data) {
          setMessages(data.messages);
          setConversationId(data.conversation_id);
          setHasMore(data.has_more);
          setNextCursor(data.next_cursor);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialHistory();
  }, [enabled, visitorId, fetchHistory]);

  // Função para forçar recarregamento do histórico (retry manual)
  const refetch = useCallback(() => {
    hasFetched.current = false;
    setMessages([]);
    setConversationId(null);
    setError(null);
    setHasMore(false);
    setNextCursor(null);
  }, []);

  // Função para carregar mais mensagens (scroll infinito)
  const loadMore = useCallback(async () => {
    // Evita chamadas duplicadas
    if (!hasMore || !nextCursor || isLoadingMore.current) return;

    isLoadingMore.current = true;
    setIsLoading(true);

    try {
      const data = await fetchHistory(nextCursor);
      
      if (data) {
        // Adiciona mensagens mais antigas no início da lista
        setMessages((prev) => [...data.messages, ...prev]);
        setHasMore(data.has_more);
        setNextCursor(data.next_cursor);
      }
    } catch (err) {
      console.error('[UaizyChat] Erro ao carregar mais mensagens:', err);
    } finally {
      setIsLoading(false);
      isLoadingMore.current = false;
    }
  }, [hasMore, nextCursor, fetchHistory]);

  return {
    messages,
    conversationId,
    isLoading,
    error,
    hasMore,
    hasStarted,  // Expor para o Widget
    loadMore,
    refetch,
  };
};
